import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { refreshTeamVault } from '../../store/teamVaultStore';
import teamVaultService, { teamVaultErrorMessage } from '../../services/teamVaultService';
import { VAULT_TYPE_META } from '../VaultItemModal';
import VaultDetailClose from '../VaultDetailClose';
import VaultSiteIcon from '../VaultSiteIcon';
import { buildFieldRows } from './vaultItemFields';
import { FieldRow } from './VaultFieldRow';

/**
 * The detail pane for a TEAM vault item.
 *
 * A separate component from `VaultItemDetail`, not a mode of it, because
 * almost everything about the two differs below the surface:
 *
 *   - THE BODY COMES FROM HTTP, not `vault_get_item`. A team id handed to that
 *     command is a lookup in the personal table for an id that cannot be there.
 *     The endpoint used here is the rate-limited, AUDITED one, which is why it
 *     is called on selection (a deliberate act of opening one credential) and
 *     never to populate a list.
 *
 *   - THERE IS NO SHARE AND NO SECRET LINK. Both are built on the personal
 *     vault's zero-knowledge model — chat shares are sealed to a recipient's
 *     published key, secret links keep the key in the URL fragment. Offering
 *     either here would imply a privacy property the team vault does not have.
 *
 *   - THERE IS NO FAVOURITE. `favorite` is a personal-vault column.
 *
 *   - WRITE IS THE SERVER'S DECISION. `permission` arrives per item, already
 *     resolved for this user; it is never re-derived from team role or from
 *     `created_by` (authoring an item grants nothing — an author who loses
 *     their grant loses access to their own item, which is BE-05, a real past
 *     vulnerability in this codebase).
 */
export function VaultTeamItemDetail({ teamId, teamName, envelope, onEdit, onClose, siteIconBase = null, siteKey = null }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [full, setFull] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  // `t` through a ref, deliberately out of the dependency array below: its
  // identity changes when the language does, and this effect is a
  // RATE-LIMITED, AUDITED credential read. Depending on it would log a second
  // read of a production secret because someone switched to German.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let alive = true;
    setData(null);
    setFull(null);
    setLoadError(null);
    setConfirmDelete(false);
    teamVaultService
      .getItem(teamId, envelope.id)
      .then((item) => {
        if (!alive) return;
        setFull(item);
        setData(item?.data || {});
      })
      .catch((e) => {
        // A revoked grant lands here as a clean 404 — the message says the
        // credential is not available, never that it does not exist.
        if (alive) setLoadError(teamVaultErrorMessage(e, tRef.current('vault.loadFailed')));
      });
    return () => {
      alive = false;
    };
  }, [teamId, envelope.id, envelope.version]);

  const meta = VAULT_TYPE_META[envelope.type] || VAULT_TYPE_META.custom;
  const TypeIcon = meta.Icon;
  const rows = data ? buildFieldRows(envelope.type, data, t) : [];
  const canWrite = envelope.permission === 'write';

  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await teamVaultService.deleteItem(teamId, envelope.id);
      await refreshTeamVault(teamId);
    } catch (e) {
      console.warn('[vault] team item delete failed:', teamVaultErrorMessage(e, e?.message));
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="vault-detail">
      <div className="vault-detail-header">
        <div className="vault-detail-icon">
          <VaultSiteIcon base={siteIconBase} siteKey={siteKey} Fallback={TypeIcon} size={16} px={20} />
        </div>
        <div className="vault-detail-titles">
          <div className="vault-detail-title">{envelope.name}</div>
          <div className="vault-detail-sub">
            {t(meta.labelKey)}
            {teamName ? ` · ${teamName}` : ''}
            {!canWrite ? ` · ${t('vault.teamReadOnly', 'Read-only')}` : ''}
          </div>
        </div>
        <div className="vault-detail-actions">
          {confirmDelete ? (
            <>
              {/* Deleting a TEAM credential removes it for everyone with
                  access — a different act from deleting your own copy of
                  something, so it says so rather than reusing the personal
                  wording. */}
              <span className="vault-confirm-label">
                {t('vault.teamDeleteConfirm', 'Delete for everyone in the team?')}
              </span>
              <button
                type="button"
                className="vault-action-btn vault-action-btn--danger"
                disabled={busy}
                onClick={doDelete}
              >
                {t('common.delete')}
              </button>
              <button
                type="button"
                className="vault-action-btn"
                disabled={busy}
                onClick={() => setConfirmDelete(false)}
              >
                {t('common.cancel')}
              </button>
            </>
          ) : (
            canWrite && (
              <>
                <button
                  type="button"
                  className="vault-action-btn"
                  title={t('common.edit')}
                  disabled={!full || busy}
                  onClick={() => full && onEdit(envelope, full)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="vault-action-btn vault-action-btn--danger"
                  title={t('common.delete')}
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 size={14} />
                </button>
              </>
            )
          )}
        </div>
        {onClose && <VaultDetailClose onClose={onClose} />}
      </div>

      <OsScroll style={{ flex: 1, minHeight: 0 }}>
        {loadError ? (
          <div className="vault-empty">
            <span className="vault-empty-sub">{loadError}</span>
          </div>
        ) : (
          <div className="vault-fields">
            {rows.map((row) => (
              <FieldRow key={`team:${envelope.id}:${row.id}`} row={row} t={t} />
            ))}
          </div>
        )}
      </OsScroll>
    </div>
  );
}
